import { useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../services/adminMessagesApi';

const tabs = [
  { label: 'All', value: '' },
  { label: 'Flagged', value: 'true' },
];

const formatDate = (ts) => (ts ? new Date(ts).toLocaleString() : 'N/A');

const MessageBubble = ({ msg }) => {
  const tone =
    msg.sender_type === 'host'
      ? 'bg-emerald-50 text-emerald-900 border border-emerald-100'
      : msg.sender_type === 'guest'
      ? 'bg-slate-100 text-slate-900 border border-slate-200'
      : 'bg-indigo-50 text-indigo-900 border border-indigo-100';

  if (msg.is_deleted) {
    return <div className="text-xs italic text-slate-500">Message removed by admin</div>;
  }

  return (
    <div className={`rounded-xl px-3 py-2 text-sm ${tone}`}>
      <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
        <span className="uppercase">{msg.sender_type}</span>
        <span>{formatDate(msg.created_at)}</span>
      </div>
      <div className="text-slate-900">{msg.body}</div>
    </div>
  );
};

const QuickLink = ({ href, label }) => {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:border-emerald-400 hover:text-emerald-700"
    >
      {label}
    </a>
  );
};

const AdminMessages = ({ token }) => {
  const [filters, setFilters] = useState({ flagged: '', q: '' });
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState({ messages: [], notes: [], events: [], booking: null, contact: null, conversation: null });
  const [loadingList, setLoadingList] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const textareaRef = useRef(null);

  const loadConversations = async (silent = false) => {
    if (!token) return;
    if (!silent) setLoadingList(true);
    setError('');
    try {
      const res = await api.listConversations(token, {
        flagged: filters.flagged,
        q: filters.q,
        limit: 50,
      });
      setConversations(res.items || []);
      if (!selectedId && (res.items || []).length) {
        setSelectedId(res.items[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load conversations');
    } finally {
      if (!silent) setLoadingList(false);
    }
  };

  const loadThread = async (id, silent = false) => {
    if (!id) return;
    if (!silent) setLoadingThread(true);
    setError('');
    try {
      const res = await api.getConversation(id, token, { limit: 200 });
      setThread({
        messages: res.messages || [],
        notes: res.notes || [],
        events: res.events || [],
        booking: res.booking,
        contact: res.contact,
        conversation: res.conversation,
        can_send_as_admin: res.can_send_as_admin,
        privacy_mode: res.privacy_mode,
      });
    } catch (err) {
      setError(err.message || 'Failed to load conversation');
    } finally {
      if (!silent) setLoadingThread(false);
    }
  };

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.flagged]);

  useEffect(() => {
    const t = setTimeout(() => loadConversations(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q]);

  useEffect(() => {
    if (selectedId) {
      loadThread(selectedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const onSendMessage = async () => {
    if (!thread.can_send_as_admin || !selectedId) return;
    const text = textareaRef.current?.value || '';
    if (!text.trim()) return;
    setSendingMessage(true);
    const optimistic = {
      id: `temp-${Date.now()}`,
      sender_type: 'admin',
      body: text,
      created_at: new Date().toISOString(),
    };
    setThread((prev) => ({ ...prev, messages: [...(prev.messages || []), optimistic] }));
    textareaRef.current.value = '';
    try {
      const saved = await api.sendMessage(selectedId, text, token);
      setThread((prev) => ({
        ...prev,
        messages: (prev.messages || []).map((m) => (m.id === optimistic.id ? saved : m)),
      }));
    } catch (err) {
      setError(err.message || 'Failed to send message');
      setThread((prev) => ({
        ...prev,
        messages: (prev.messages || []).filter((m) => m.id !== optimistic.id),
      }));
    } finally {
      setSendingMessage(false);
    }
  };

  const onAddNote = async () => {
    if (!note.trim() || !selectedId) return;
    setSendingNote(true);
    try {
      const created = await api.addNote(selectedId, note, token);
      setThread((prev) => ({ ...prev, notes: [...(prev.notes || []), created] }));
      setNote('');
    } catch (err) {
      setError(err.message || 'Failed to add note');
    } finally {
      setSendingNote(false);
    }
  };

  const onDeleteMessage = async (messageId) => {
    try {
      await api.deleteMessage(messageId, token);
      await loadThread(selectedId, true);
    } catch (err) {
      setError(err.message || 'Failed to delete message');
    }
  };

  const onUnflag = async () => {
    try {
      await api.unflag(selectedId, token);
      await loadThread(selectedId, true);
      await loadConversations(true);
    } catch (err) {
      setError(err.message || 'Failed to mark resolved');
    }
  };

  const onMute = async (target, muted) => {
    try {
      await api.mute(selectedId, target, muted, token);
      await loadThread(selectedId, true);
    } catch (err) {
      setError(err.message || 'Failed to update mute status');
    }
  };

  const onExport = async () => {
    try {
      const data = await api.exportConversation(selectedId, token);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `conversation-${selectedId}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to export conversation');
    }
  };

  const currentConversation = useMemo(
    () => conversations.find((c) => c.id === selectedId) || thread.conversation,
    [conversations, selectedId, thread.conversation]
  );

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Support</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Host-Guest Messages</h1>
          <p className="text-sm text-slate-600">
            Read, audit, and act on conversations. Privacy mode: {thread.privacy_mode ? 'Restricted' : 'Standard'}.
          </p>
        </div>
        <div className="flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.value || 'all'}
              type="button"
              onClick={() => setFilters((f) => ({ ...f, flagged: tab.value }))}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                filters.flagged === tab.value ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 grid gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <input
            type="search"
            placeholder="Search booking/guest/property"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contact options</p>
            {selectedId && thread.contact ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <QuickLink href={thread.contact.quick_links?.email} label={thread.contact.guest_email || 'Email'} />
                <QuickLink href={thread.contact.quick_links?.phone} label={thread.contact.guest_phone || 'Call'} />
                <QuickLink href={thread.contact.quick_links?.whatsapp} label="WhatsApp" />
              </div>
            ) : (
              <p className="mt-2 text-xs text-slate-500">Open a conversation to see guest contact links.</p>
            )}
          </div>
          <div className="mt-3 h-[620px] space-y-2 overflow-y-auto">
            {loadingList && <div className="text-sm text-slate-500">Loading conversations...</div>}
            {!loadingList && conversations.length === 0 && (
              <div className="text-sm text-slate-500">No conversations found.</div>
            )}
            {conversations.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelectedId(c.id)}
                className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                  selectedId === c.id ? 'border-emerald-300 bg-white' : 'border-slate-200 bg-white hover:border-emerald-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900">
                    {c.guest_name || 'Guest'} | {c.booking_code}
                  </div>
                  {c.admin_flagged && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                      Flagged
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">{c.property_title}</div>
                <div className="line-clamp-2 text-xs text-slate-600">{c.last_message_preview || 'No messages'}</div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {formatDate(c.last_message_at)} | Host unread: {c.host_unread_count} | Guest unread:{' '}
                  {c.guest_unread_count}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-4">
          {!selectedId && <div className="text-sm text-slate-500">Select a conversation to view messages.</div>}
          {selectedId && (
            <div className="flex h-full flex-col gap-3">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 pb-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {currentConversation?.guest_name || 'Guest'} (Guest) | {currentConversation?.host_name || 'Host'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {currentConversation?.property_title || 'Property'} | {currentConversation?.booking_code} |{' '}
                    {currentConversation?.check_in_date} - {currentConversation?.check_out_date}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500">
                    <QuickLink href={thread.contact?.quick_links?.email} label="Email guest" />
                    <QuickLink href={thread.contact?.quick_links?.phone} label="Call guest" />
                    <QuickLink href={thread.contact?.quick_links?.whatsapp} label="WhatsApp" />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={onExport}
                    className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Export JSON
                  </button>
                  <button
                    type="button"
                    onClick={onUnflag}
                    className="rounded-lg border border-emerald-300 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                  >
                    Mark resolved
                  </button>
                </div>
              </div>

              {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
              <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                <span className="rounded-full bg-slate-100 px-2 py-1">
                  Host muted: {thread.conversation?.host_muted ? 'Yes' : 'No'}
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-1">
                  Guest muted: {thread.conversation?.guest_muted ? 'Yes' : 'No'}
                </span>
                <button
                  type="button"
                  onClick={() => onMute('host', !thread.conversation?.host_muted)}
                  className="rounded-full border border-slate-200 px-2 py-1"
                >
                  {thread.conversation?.host_muted ? 'Unmute host' : 'Mute host'}
                </button>
                <button
                  type="button"
                  onClick={() => onMute('guest', !thread.conversation?.guest_muted)}
                  className="rounded-full border border-slate-200 px-2 py-1"
                >
                  {thread.conversation?.guest_muted ? 'Unmute guest' : 'Mute guest'}
                </button>
              </div>

              <div className="grid gap-3 lg:grid-cols-3">
                <div className="lg:col-span-2 space-y-2 rounded-lg bg-slate-50 p-3 h-[420px] overflow-y-auto">
                  {loadingThread && <div className="text-sm text-slate-500">Loading messages...</div>}
                  {!loadingThread && (thread.messages || []).length === 0 && (
                    <div className="text-sm text-slate-500">No messages yet.</div>
                  )}
                  {(thread.messages || []).map((m) => (
                    <div key={m.id} className="relative group">
                      <MessageBubble msg={m} />
                      <button
                        type="button"
                        onClick={() => onDeleteMessage(m.id)}
                        className="invisible absolute right-0 top-0 rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-rose-600 shadow group-hover:visible"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>

                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-sm font-semibold text-slate-900">Internal notes</p>
                  <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                    {(thread.notes || []).map((n) => (
                      <div key={n.id} className="rounded-lg bg-slate-50 p-2 text-sm">
                        <div className="text-xs text-slate-500">{formatDate(n.created_at)}</div>
                        <div className="text-slate-800">{n.note}</div>
                      </div>
                    ))}
                    {(thread.notes || []).length === 0 && <div className="text-sm text-slate-500">No notes yet.</div>}
                  </div>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    rows={3}
                    placeholder="Add internal note"
                  />
                  <button
                    type="button"
                    onClick={onAddNote}
                    disabled={sendingNote}
                    className="mt-2 w-full rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                  >
                    {sendingNote ? 'Saving...' : 'Add note'}
                  </button>
                </div>
              </div>

              {thread.can_send_as_admin && (
                <div className="rounded-lg border border-slate-200 p-3 shadow-sm">
                  <textarea
                    ref={textareaRef}
                    className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    rows={3}
                    placeholder="Send message as admin"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        onSendMessage();
                      }
                    }}
                  />
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                    <span>Admin messages are tagged separately; hosts/guests cannot reply as admin.</span>
                    <button
                      type="button"
                      onClick={onSendMessage}
                      disabled={sendingMessage}
                      className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {sendingMessage ? 'Sending...' : 'Send as admin'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;
